<template>
  <div v-if="isMobileApp">
    <div class="flex gap-2 w-full">
      <!-- Watch Status Button -->
      <button
        class="flex-1 flex flex-col items-center gap-1 py-1 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5"
        :class="statusButtonClass"
        @click="handleStatusClick">
        <fa-icon :icon="statusIcon" class="text-xl" :class="statusIconClass" />
        <span class="text-2xs font-bold text-white/90">{{ statusLabel }}</span>
      </button>

      <!-- Review Button -->
      <button
        class="flex-1 flex flex-col items-center gap-1 py-1 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 hover:border-yellow-400 hover:border-b-4 hover:-mt-0.5"
        :class="
          isReviewed
            ? 'bg-yellow-500/20 hover:bg-white/30 border-white/40'
            : 'border-white/20 bg-white/10 hover:bg-white/20'
        "
        @click="handleReviewClick">
        <fa-icon
          :icon="
            isReviewed ? ['fas', 'hundred-points'] : ['far', 'hundred-points']
          "
          class="text-xl"
          :class="isReviewed ? 'text-yellow-400' : 'text-white/60'" />
        <span class="text-2xs font-bold text-white/90">
          {{ reviewLabel }}
        </span>
      </button>

      <!-- Add to List Button -->
      <button
        class="flex-1 flex flex-col items-center gap-1 py-1 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 hover:border-blue-400 hover:border-b-4 hover:-mt-0.5"
        :class="
          isInAnyList
            ? 'bg-blue-500/20 hover:bg-white/30 border-white/40'
            : 'bg-white/10 hover:bg-white/20 border-white/20'
        "
        @click="handleListsClick">
        <fa-icon
          :icon="isInAnyList ? ['fas', 'list-ol'] : ['far', 'list-ol']"
          class="text-xl"
          :class="isInAnyList ? 'text-blue-400' : 'text-white/60'" />
        <span class="text-2xs font-bold text-white/90">
          {{ listLabel }}
        </span>
      </button>

      <!-- Favourite Button -->
      <button
        class="flex-1 flex flex-col items-center gap-1 py-1 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 hover:border-red-400 hover:border-b-4 hover:-mt-0.5"
        :class="
          isFaved
            ? 'bg-red-500/20 hover:bg-white/30 border-white/40'
            : 'bg-white/10 hover:bg-white/20 border-white/20'
        "
        @click="handleFaveClick">
        <fa-icon
          :icon="isFaved ? 'heart' : ['far', 'heart']"
          class="text-xl"
          :class="isFaved ? 'text-red-400' : 'text-white/60'" />
        <span class="text-2xs font-bold text-white/90">
          {{ isFaved ? 'Favourited' : 'Favourite' }}
        </span>
      </button>
    </div>

    <!-- Add to List Sheet -->
    <AddToListSheet
      v-model="showAddToList"
      :title="title"
      :title-info="titleInfo"
      :fullscreen="fullscreenModals" />

    <!-- Watch Status Sheet -->
    <StatusQuickSheet
      v-model="showStatusSheet"
      :title="title"
      :current-log="currentLog"
      :title-type="titleType"
      :title-name="
        titleInfo?.title || titleInfo?.name || title?.title || title?.name || ''
      "
      :fullscreen="fullscreenModals"
      @status-changed="handleStatusChanged" />

    <!-- Favourites Sheet -->
    <FavouritesSheet
      v-model="showFavouritesSheet"
      :title="title"
      :fullscreen="fullscreenModals"
      :newly-added-tmdb-id="newlyAddedTmdbId" />

    <!-- Review Sheet (non-fullscreen mode) -->
    <Sheet
      v-model="showReviewSheet"
      title="Review"
      size="lg"
      tall
      content-class="p-0">
      <ReviewForm
        :title="title"
        :review-type="titleType"
        :show-drag-handle="false"
        @close="showReviewSheet = false" />
    </Sheet>
  </div>
</template>

<script setup>
import { computed, ref, watch, inject } from 'vue'
import { useRouter } from 'vue-router'
import { useTitleActions } from '@/use/useTitleActions'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreAuth } from '@/stores/storeAuth'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import { useStoreProfile } from '@/stores/storeProfile'
import {
  AddToListSheet,
  StatusQuickSheet,
  FavouritesSheet
} from '@/components/Sheets'
import { Sheet } from '@/components/Options'
import ReviewForm from '@/components/ReviewForm'
import { format } from 'date-fns'
import confetti from 'canvas-confetti'

const { isMobileApp } = inject('platform')
const router = useRouter()
const haptics = useHapticFeedback()
const storeProfile = useStoreProfile()

const fullscreenModals = computed(() => {
  return (
    storeProfile.getCurrentUserProfile?.feature_flags?.fullscreen_modals ===
    true
  )
})

// Heart emoji shapes for confetti
const heartEmojis = ['❤️', '💖', '💕', '💗', '💓']
const heartShapes = heartEmojis.map((emoji) =>
  confetti.shapeFromText({ text: emoji })
)

const props = defineProps({
  title: {
    type: Object,
    required: true
  },
  titleInfo: {
    type: Object,
    default: null
  }
})

const storeLists = useStoreLists()
const storeTitlesUser = useStoreTitlesUser()
const storeTitleLogs = useStoreTitleLogs()
const storeReviews = useStoreReviews()
const storeAuth = useStoreAuth()

// Sheet state
const showAddToList = ref(false)
const showStatusSheet = ref(false)
const showFavouritesSheet = ref(false)
const newlyAddedTmdbId = ref(null)

// Compute title type
const titleType = computed(() => {
  return props.title?.media_type || props.titleInfo?.tmdb_type || 'movie'
})

// Get title ID for store lookups
const titleId = computed(() => {
  return props.titleInfo?.id ?? null
})

// Watch for title changes
watch(titleId, (newValue) => {
  if (newValue) {
    storeTitlesUser.fetchInfoByTId(newValue)
  }
})

// Get user title info from store
const userTitleInfo = computed(() => {
  return storeTitlesUser.getTitleByTId(titleId.value)
})

// Actions computed
const actionsComputed = computed(() => ({
  title: props.title,
  titleInfo: props.titleInfo,
  userTitleInfo: userTitleInfo.value,
  type: titleType.value
}))

const {
  openReviewCreateModal,
  showReviewSheet,
  toggleFaved,
  isSeen,
  isFaved,
  isReviewed,
  isWatchlisted,
  isInProgress,
  usesLogSystem
} = useTitleActions(actionsComputed)

// Get user's review for this title
const currentUserId = computed(() => storeAuth.user?.id)
const userReview = computed(() => {
  if (!currentUserId.value || !props.title?.id) return null
  const userId = currentUserId.value
  const tmdbId = props.title.id
  if (!storeReviews.reviews || !Array.isArray(storeReviews.reviews)) return null
  const reviews = storeReviews.reviews.filter(
    (review) =>
      review &&
      review.user_id === userId &&
      (review.tmdb_id == tmdbId || review.titles?.tmdb_id == tmdbId)
  )
  return reviews.length > 0 ? reviews[0] : null
})

// Format user's review score
const reviewLabel = computed(() => {
  if (!isReviewed.value) return 'Review'
  const score = userReview.value?.score_overall
  if (score != null) {
    return Number(score).toFixed(1)
  }
  return 'Reviewed'
})

// Get current log for status sheet
const currentLog = computed(() => {
  if (!usesLogSystem.value) return null
  const tmdbId = props.title?.id
  const tmdbType = titleType.value
  if (!tmdbId || !tmdbType) return null
  return storeTitleLogs.getLatestLogByTmdb(tmdbId, tmdbType)
})

// Current status
const currentStatus = computed(() => {
  // Check log state first for dropped (not exposed via boolean flags)
  if (currentLog.value?.state === 'dropped') return 'dropped'
  if (isSeen.value) return 'seen'
  if (isInProgress.value) return 'in_progress'
  if (isWatchlisted.value) return 'watchlist'
  return null
})

// Status button display properties
const statusIcon = computed(() => {
  switch (currentStatus.value) {
    case 'seen':
      return ['fas', 'hexagon-check']
    case 'in_progress':
      return ['fas', 'play-circle']
    case 'watchlist':
      return ['fas', 'bookmark']
    case 'dropped':
      return ['fas', 'circle-stop']
    default:
      return ['far', 'bookmark']
  }
})

// Get watched date from log
const watchedDate = computed(() => {
  if (!currentLog.value || currentLog.value.state !== 'seen') return null
  const dateField = currentLog.value.watched_on
  if (!dateField) return null
  try {
    return format(new Date(dateField), 'MMM d')
  } catch (e) {
    return null
  }
})

const statusLabel = computed(() => {
  switch (currentStatus.value) {
    case 'seen':
      return watchedDate.value || 'Watched'
    case 'in_progress':
      return 'Watching'
    case 'watchlist':
      return 'Watchlist'
    case 'dropped':
      return 'DNF'
    default:
      return 'Status'
  }
})

const statusIconClass = computed(() => {
  switch (currentStatus.value) {
    case 'seen':
      return 'text-green-400'
    case 'in_progress':
      return 'text-cyan-400'
    case 'watchlist':
      return 'text-amber-400'
    case 'dropped':
      return 'text-red-400'
    default:
      return 'text-white/60'
  }
})

const statusButtonClass = computed(() => {
  switch (currentStatus.value) {
    case 'seen':
      return 'bg-green-500/20 hover:bg-white/30 border-white/40'
    case 'in_progress':
      return 'bg-cyan-500/20 hover:bg-white/30 border-white/40'
    case 'watchlist':
      return 'bg-amber-500/20 hover:bg-white/30 border-white/40'
    case 'dropped':
      return 'bg-red-500/20 hover:bg-white/30 border-white/40'
    default:
      return 'bg-white/10 hover:bg-white/20 border-white/20 hover:border-green-400 hover:border-b-4 hover:-mt-0.5'
  }
})

// List items
const listItems = computed(() => {
  if (!titleId.value) return []
  return storeLists.getListItemsByTitle(titleId.value)
})

const isInAnyList = computed(() => {
  return listItems.value.length > 0
})

const listLabel = computed(() => {
  const count = listItems.value.length
  if (count === 0) return 'Lists'
  return `${count} List${count > 1 ? 's' : ''}`
})

// Handlers
const handleStatusClick = () => {
  haptics.light()
  showStatusSheet.value = true
}

const handleStatusChanged = () => {
  // StatusQuickSheet handles all the status logic internally
}

const handleFaveClick = async (event) => {
  haptics.light()

  // Trigger heart confetti when favoriting (not when unfavoriting)
  if (!isFaved.value && event?.target) {
    const rect = event.target.getBoundingClientRect()
    const x = (rect.left + rect.width / 2) / window.innerWidth
    const y = (rect.top + rect.height / 2) / window.innerHeight

    confetti({
      particleCount: 25,
      spread: 70,
      origin: { x, y },
      shapes: heartShapes,
      scalar: 1.8,
      gravity: 0.9,
      drift: 0,
      ticks: 120,
      startVelocity: 25,
      disableForReducedMotion: true
    })
  }

  await toggleFaved()
}

const handleReviewClick = () => {
  haptics.light()
  if (isReviewed.value && userReview.value?.id) {
    router.push({
      name: 'WebReviewsSingle',
      params: { id: userReview.value.id }
    })
  } else {
    openReviewCreateModal()
  }
}

const handleListsClick = () => {
  haptics.light()
  showAddToList.value = true
}
</script>
